import React, {useContext, useEffect, useRef, useState} from 'react';
import {Button, joinClassNames, MessagePopupCore, SegmentedButton, SelectInput, Size, TextArea} from "@dts-soldel/dts-react-common";
import LocalStorage from "../../../app/localstorage/LocalStorage";
import ConfigurationTypes from "../ConfigurationTypes";
import ConfiguratorOutputHeader from "./ConfiguratorOutputHeader";
import PropTypes from "prop-types";
import ConfiguratorOutputMenu from "./ConfiguratorOutputMenu";
import MobXContext from "../../../app/mobx/MobXContext";

const propTypes = {
	configurationType: PropTypes.oneOf(Object.values(ConfigurationTypes)).isRequired,
};
const defaultProps = {};

const formatRawOutput = (node) => {
	let retVal;

	//get the raw text from the dom node
	retVal = node ? node.textContent : '';

	//add new lines between each indent of 4 or more spaces
	retVal = retVal.replace(/\s{4,}/g, '\n$&');

	//format the the script tags
	retVal = retVal.replace(/(<script>)/g, '\n\n$&');
	retVal = retVal.replace(/(<\/script>)/g, '\n$&');

	// //format the if statement
	retVal = retVal.replace(/(\s{4}if \(window.utahHeader)/g, '\n$&');

	return retVal;
};

const ConfiguratorOutput = ({configurationType}) => {
	const {appStore} = useContext(MobXContext);
	const [,forceRerender] = useState();
	const [outputStyle, setOutputStyle] = useState(LocalStorage.configuratorOutputType.get("pretty"));
	const outputPreRef = useRef();
	const rerenderIntervalId = useRef(NaN);

	useEffect(() => {
		setOutputStyle('pretty');
	}, [configurationType]);

	useEffect(() => {
		rerenderIntervalId.current = setInterval(forceRerender, 1000);
		return () => clearInterval(forceRerender.current);
	}, []);

	return (
		<div className="configurator__outputs">
			<div className="configurator__content__panel-left__top">
				<SelectInput
					field="version"
					label="Version"
					options={
						((appStore && appStore.versions && appStore.versions.versions) || [])
							.map(version => ({label: version.replace(/_/g, '.'), value: version}))
					}
					className="versions-select"
					allowClear={false}
					value={appStore.currentVersion}
					onChange={(field, value) => {
						if (value !== appStore.currentVersion) {
							MessagePopupCore.addMessage({
								title: 'Switch Utah Header Version',
								message: 'Switching the version may reset your configuration settings.',
								closeCallback: (message, buttonText) => (buttonText === 'Okay') && (window.location = `https://cdn.utah.gov/utah-header/${value}/examples/index.html`),
								hasCancelButton: true,
							});
						}
					}}
					size={Size.MEDIUM}
				/>
				<Button
					onClick={() => {
						if (outputPreRef.current) {
							// https://stackoverflow.com/questions/3665115/how-to-create-a-file-in-memory-for-user-to-download-but-not-through-server
							const element = document.createElement('a');
							element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(formatRawOutput(outputPreRef.current, configurationType)));
							element.setAttribute('download', 'utahHeaderConfig.js');
							element.style.display = 'none';
							document.body.appendChild(element);

							element.click();

							document.body.removeChild(element);
						}
					}}
					label="download"
					disabled={!outputPreRef.current}
				/>
				<Button
					onClick={() => {
						if (outputPreRef.current) {
							// https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/writeText
							// this only works on LOCALHOST or domains that are HTTPS
							navigator.clipboard.writeText(formatRawOutput(outputPreRef.current, configurationType))
								.then(() => MessagePopupCore.addMessage({title: 'Configuration Copied!', message: 'This configuration has been copied to your copy/paste clipboard'}));
						}
					}}
					label="copy"
					disabled={!outputPreRef.current}
				/>
			</div>
			<SegmentedButton
				className="segmented-button full-width"
				field="output-style"
				label="Output Style"
				showLabel={false}
				options={[
					{label: "Pretty", value: 'pretty'},
					{label: "Raw", value: 'raw'},
				]}
				value={outputStyle}
				onChange={(field, value) => {
					setOutputStyle(value);
					LocalStorage.configuratorOutputType.set(value);
				}}
			/>

			<div className={joinClassNames('configurator__output-wrapper', outputStyle === 'pretty' ? '' : 'hidden')}>
				<pre ref={outputPreRef}>
					{configurationType === ConfigurationTypes.header ? <ConfiguratorOutputHeader/> : undefined}
					{configurationType === ConfigurationTypes.menu ? <ConfiguratorOutputMenu/> : undefined}
				</pre>
			</div>

			{
				outputStyle === 'raw' ?
					<>
						<TextArea
							className="textarea-fullsize configurator__output-raw"
							field="raw-output"
							showLabel={false}
							label="Raw Output"
							value={formatRawOutput(outputPreRef.current, configurationType)}
							readOnly={true}
						/>
					</>
					: undefined
			}
		</div>
	);
};

ConfiguratorOutput.propTypes = propTypes;
ConfiguratorOutput.defaultProps = defaultProps;

export default ConfiguratorOutput;
